import React, { useState, useEffect, useRef } from 'react';
import { User } from 'lucide-react';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import LoadMoreButton from '../ui/LoadMoreButton';
import { formatHours } from '../../utils/helpers';

const StudentHoursChart = ({ 
  studentHoursData, 
  sortOrder, 
  setSortOrder
}) => {
  const [displayLimit, setDisplayLimit] = useState(50);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // 当排序改变时，重置显示数量以便从头查看
  useEffect(() => {
    setDisplayLimit(50);
  }, [sortOrder]);

  // 处理加载更多
  const handleLoadMore = () => {
    if (loadingMore || displayLimit >= studentHoursData.length) return;
    
    setLoadingMore(true);
    // 增加显示数量
    setTimeout(() => {
      setDisplayLimit(prev => prev + 50);
      setLoadingMore(false);
    }, 300);
  };

  // 使用 IntersectionObserver 实现滚动自动加载
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && displayLimit < studentHoursData.length) {
          console.log('📥 学生课时触发自动加载更多');
          handleLoadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' } // 提前 100px 触发
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadingMore, displayLimit, studentHoursData.length]);

  // 按排序顺序处理学生数据
  const sortedStudentHoursData = [...studentHoursData].sort((a, b) => {
    const aHours = a.remaining_hours || 0;
    const bHours = b.remaining_hours || 0;
    return sortOrder === 'desc' ? bHours - aHours : aHours - bHours;
  });

  // 当前显示的数据分片
  const visibleData = sortedStudentHoursData.slice(0, displayLimit);
  const hasMore = displayLimit < sortedStudentHoursData.length;

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <User size={16} className="text-[#3182CE]" />
          <h2 className="font-bold text-base text-[#2D3748]">学生课时</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setSortOrder('desc')}
            className={`px-3 py-1 text-sm rounded-md font-bold transition-all ${sortOrder === 'desc' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            最多
          </button>
          <button
            onClick={() => setSortOrder('asc')}
            className={`px-3 py-1 text-sm rounded-md font-bold transition-all ${sortOrder === 'asc' ? 'bg-blue-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            最少
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {visibleData.map((student) => (
          <div key={student.id} className="bg-white p-2 rounded-lg border-[#EDF2F7] border shadow-sm">
            <div className="flex flex-col gap-1 mb-1.5 min-w-0">
              <div className="text-sm font-bold text-[#2D3748] truncate">{student.name}</div>
              <div className="inline-flex self-start px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 text-[11px] font-black">
                {formatHours(student.remaining_hours || 0)} 节
              </div>
            </div>
            
            <div className="mt-1">
              <div className="text-[10px] text-[#A0AEC0] mb-1 font-bold uppercase">最近6个月消耗</div>
              <div className="flex gap-1">
                {(student.recent_months_consumption || []).map((hours, idx) => (
                  <div key={idx} className="flex-1">
                    <div className="text-[9px] text-center text-[#718096] scale-90 origin-bottom">
                      {(() => {
                        const now = new Date();
                        const currentMonth = now.getMonth() + 1;
                        let calculatedMonth = currentMonth - idx;
                        if (calculatedMonth <= 0) {
                          calculatedMonth = 12 + calculatedMonth;
                        }
                        return calculatedMonth + '月';
                      })()}
                    </div>
                    <div className="mt-1 h-6 bg-gray-50 rounded flex items-end overflow-hidden border border-gray-100">
                      <div
                        className="bg-green-400 w-full rounded-t-sm transition-all duration-500"
                        style={{ height: `${Math.min(100, (hours / Math.max(...(student.recent_months_consumption || [0]), 1)) * 100)}%` }}
                      ></div>
                    </div>
                    <div className="text-[9px] text-center text-[#4A5568] mt-1 font-bold">{formatHours(hours)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* 加载更多控件 */}
      <div className="mt-4">
        <LoadMoreButton 
          onClick={handleLoadMore}
          loading={loadingMore}
          hasMore={hasMore}
        />
        {/* 自动加载哨兵 */}
        <div ref={sentinelRef} className="h-4 w-full" />
      </div>
    </Card>
  );
};

export default StudentHoursChart;